import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import DataTable from '../components/common/DataTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import '../styles/ReviewsPage.css';
import { getReviews, moderateReview } from '../utils/api';
import {
  Star,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Flag,
  CheckCircle,
  Eye,
  User,
  Briefcase,
  ShieldCheck,
  Sparkles,
  X
} from 'lucide-react';

const ReviewsPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const { success, error } = useToast();

  const { data: reviews, isLoading, execute: fetchReviews } = useApi(getReviews, { immediate: true });

  const handleModerate = async (reviewId, action) => {
    try {
      await moderateReview(reviewId, action);
      success(`Review ${action === 'approve' ? 'aprovada' : 'rejeitada'}!`);
      fetchReviews();
    } catch (err) {
      error('Erro ao moderar review');
    }
  };

  // Avaliações são guardadas em pontos 1-10; 2 pontos = 1 estrela.
  const ratingStars = (points) => {
    const stars = (Number(points) || 0) / 2;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} className={`w-4 h-4 ${i <= Math.round(stars) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
        ))}
        <span className="ml-1 text-xs text-gray-400">{stars.toFixed(1)}</span>
      </div>
    );
  };

  const statusBadge = (status) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium
      ${status === 'approved' ? 'bg-green-500/20 text-green-400' :
        status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
        'bg-red-500/20 text-red-400'}`}>
      {status === 'approved' ? 'Aprovada' : status === 'pending' ? 'Pendente' : 'Rejeitada'}
    </span>
  );

  const tabs = [
    { id: 'all', label: 'Todas', count: (reviews || []).length },
    { id: 'pending', label: 'Pendentes', count: (reviews || []).filter(r => r.status === 'pending').length },
    { id: 'approved', label: 'Aprovadas', count: (reviews || []).filter(r => r.status === 'approved').length },
    { id: 'flagged', label: 'Sinalizadas', count: (reviews || []).filter(r => r.is_flagged).length },
  ];

  const columns = [
    { key: 'id', label: 'ID', width: '60px' },
    { key: 'client_name', label: 'Cliente', render: (row) => (
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-gray-500" />
        <span>{row.client_name}</span>
      </div>
    )},
    { key: 'worker_name', label: 'Profissional', render: (row) => (
      <div className="flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-gray-500" />
        <span>{row.worker_name}</span>
      </div>
    )},
    { key: 'rating', label: 'Avaliação', render: (row) => ratingStars(row.rating) },
    { key: 'comment', label: 'Comentário', render: (row) => (
      <span className="truncate max-w-[250px] block" title={row.comment}>{row.comment}</span>
    )},
    { key: 'status', label: 'Status', render: (row) => statusBadge(row.status) },
    { key: 'is_flagged', label: 'Sinalizado', render: (row) => (
      row.is_flagged ? (
        <span className="flex items-center gap-1 text-red-400 text-xs">
          <Flag className="w-3.5 h-3.5" />
          Sim
        </span>
      ) : (
        <span className="text-gray-500 text-xs">Não</span>
      )
    )},
    { key: 'created_at', label: 'Data', render: (row) =>
      row.created_at ? new Date(row.created_at).toLocaleDateString('pt-PT') : '-'
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.status === 'pending' && (
            <>
              <button
                onClick={() => handleModerate(row.id, 'approve')}
                className="p-1.5 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                title="Aprovar"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleModerate(row.id, 'reject')}
                className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                title="Rejeitar"
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={() => setSelectedReview(row)} className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors" title="Ver avaliação">
            <Eye className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const getTabData = () => {
    if (!reviews) return [];
    if (activeTab === 'all') return reviews;
    if (activeTab === 'flagged') return reviews.filter(r => r.is_flagged);
    return reviews.filter(r => r.status === activeTab);
  };

  // Stats — pontos 1-10 convertidos para estrelas (÷2)
  const avgRating = reviews?.length
    ? (reviews.reduce((acc, r) => acc + (r.rating || 0), 0) / reviews.length / 2).toFixed(1)
    : '0.0';

  return (
    <div className="reviews-admin-page space-y-6">
      <section className="reviews-admin-hero">
        <div>
          <span className="reviews-admin-eyebrow"><ShieldCheck className="w-4 h-4" /> Confiança e qualidade</span>
          <h1>Avaliações</h1>
          <p>Acompanhe a satisfação dos clientes e modere a reputação da rede Dozy com transparência.</p>
        </div>
        <div className="reviews-score-card"><Sparkles /><span><small>Reputação média</small><strong>{avgRating}<i>/5</i></strong><em>{(reviews || []).length} avaliações recebidas</em></span></div>
      </section>

      {/* Stats */}
      <div className="reviews-admin-stats grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="reviews-stat reviews-stat--amber">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg"><Star className="w-5 h-5 text-yellow-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{avgRating}</p>
              <p className="text-gray-400 text-sm">Média Geral</p>
            </div>
          </div>
        </div>
        <div className="reviews-stat reviews-stat--blue">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg"><MessageSquare className="w-5 h-5 text-blue-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(reviews || []).length}</p>
              <p className="text-gray-400 text-sm">Total Reviews</p>
            </div>
          </div>
        </div>
        <div className="reviews-stat reviews-stat--green">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg"><ThumbsUp className="w-5 h-5 text-green-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(reviews || []).filter(r => r.rating >= 8).length}</p>
              <p className="text-gray-400 text-sm">Positivas (4-5★)</p>
            </div>
          </div>
        </div>
        <div className="reviews-stat reviews-stat--red">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-lg"><Flag className="w-5 h-5 text-red-400" /></div>
            <div>
              <p className="text-2xl font-bold text-white">{(reviews || []).filter(r => r.is_flagged).length}</p>
              <p className="text-gray-400 text-sm">Sinalizadas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="reviews-admin-tabs-shell">
        <div className="reviews-admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'is-active' : ''}
            >
              {tab.label}
              <span>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <section className="reviews-admin-table">
      {isLoading && !reviews ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <DataTable
          data={getTabData()}
          columns={columns}
          pageSize={10}
          emptyMessage="Nenhuma avaliação encontrada."
        />
      )}
      </section>

      {selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm" onClick={() => setSelectedReview(null)}>
          <div className="reviews-detail-modal" onClick={e => e.stopPropagation()}>
            <button className="reviews-detail-modal__close" onClick={() => setSelectedReview(null)}><X /></button>
            <span className="reviews-admin-eyebrow">Detalhe da avaliação</span>
            <h3>{selectedReview.worker_name}</h3>
            <div className="reviews-detail-modal__rating">{ratingStars(selectedReview.rating)} {statusBadge(selectedReview.status)}</div>
            <blockquote>{selectedReview.comment || 'Sem comentário escrito.'}</blockquote>
            <div className="reviews-detail-modal__meta"><span>Cliente<strong>{selectedReview.client_name || '—'}</strong></span><span>Data<strong>{selectedReview.created_at ? new Date(selectedReview.created_at).toLocaleDateString('pt-PT') : '—'}</strong></span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;
